import { useEffect } from "react";
import moment from "moment";
import PropTypes from 'prop-types';

// @mui material components
import Grid from "@mui/material/Grid";
import { Box, Card, Tooltip } from "@mui/material";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
// Data
import { Link, useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import ClearwayCalendar from "components/Calendars/ClearwayCalendar";

import { useAuth0 } from "@auth0/auth0-react";
import SoftButton from "components/SoftComponents/SoftButton";

import clearwaysAPI from "modules/clearways/api";
import { useSoftUIController } from "hooks/context";
import { disableMap } from "utils/map";
import { wrapper, loadingScreen } from "utils/wrapper";

function ClearwaysEventsView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    clearwaysAPI.loadToken(useAuth0());

    const canClearwaysEventAdd = userPermissions.indexOf('clearways-calendar:add') >= 0;

    const navigate = useNavigate();

    const onEventClick = function (info) {
        let path = '/clearways/events/' + info.event.extendedProps.event_id;
        navigate(path);
    }

    const date = moment();
    const month = date.month();
    const year = date.year();

    const clearways = useQuery({
        queryKey: ['getClearwaysWithYearAndMonth', year, month, selectedOrg],
        queryFn: async () => {
            return clearwaysAPI.getClearwaysWithYearAndMonth(year, month)
        },
    });

    if (clearways.isLoading) {
        return loadingScreen(controller, true);
    }
    else if (clearways.error) {
        return wrapper('Error!', controller);
    }
    else if (!clearways.data) {
        return loadingScreen(controller, false);
    }

    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
        >
            <SoftBox mt={1} display="flex" sx={{ height: `calc(100% - 16px)` }}>
                <SoftBox py={0} px={1} width={"calc(100% - 16px)"} height="auto">
                    <Card sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].content, 1), height: '100%' })}>
                        <SoftBox py={1} px={deviceLayout == 'mobile' ? 1 : 2} sx={({ palette, functions: { rgba } }) => ({ borderRadius: '16px 16px 0 0', backgroundColor: rgba(palette[appearance].header, 1), borderBottom: `1px solid ${rgba(palette[appearance].border, 1)}` })}>
                            <SoftBox display='flex' justifyContent="space-between" alignItems="center">
                                <SoftTypography variant={deviceLayout == 'mobile' ? "h5" : "h4"}>Clearways Calendar</SoftTypography>
                                {canClearwaysEventAdd && (
                                    <SoftBox display='flex' flexDirection='row' justifyContent={{ xxs: 'center', md: 'flex-end' }}>
                                        <Link to={'/clearways/event'} style={{ textDecoration: 'none' }}>
                                            <SoftButton variant='gradient' color='dark' size={deviceLayout == 'mobile' ? "small" : "medium"}>{deviceLayout == 'mobile' ? "Add" : "Add Event"}</SoftButton>
                                        </Link>
                                    </SoftBox>
                                )}
                            </SoftBox>
                        </SoftBox>
                        <SoftBox m={1} sx={({ typography: { fontFamily } }) => ({ fontFamily: fontFamily, borderRadius: '16px', border: '1px solid #ddd', background: '#fff' })}>
                            <ClearwayCalendar date={date.format("YYYY-MM-DD")} data={clearways.data} onEventClick={onEventClick} />
                        </SoftBox>
                    </Card>
                </SoftBox>
            </SoftBox>
        </SoftBox>
    ), controller);
}

ClearwaysEventsView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default ClearwaysEventsView;